import { useEffect } from 'react';
import { X, AlertCircle, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export interface ToastProps {
  id: string;
  message: string;
  type: 'error' | 'warning' | 'success' | 'info';
  onClose: () => void;
  duration?: number;
}

export default function Toast({ message, type, onClose, duration = 5000 }: ToastProps) {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const bgColor =
    type === 'error'
      ? 'bg-red-50 border-red-200'
      : type === 'warning'
      ? 'bg-yellow-50 border-yellow-200'
      : type === 'success'
      ? 'bg-green-50 border-green-200'
      : 'bg-blue-50 border-blue-200';

  const textColor =
    type === 'error'
      ? 'text-red-900'
      : type === 'warning'
      ? 'text-yellow-900'
      : type === 'success'
      ? 'text-green-900'
      : 'text-blue-900';

  const iconColor =
    type === 'error'
      ? 'text-red-600'
      : type === 'warning'
      ? 'text-yellow-600'
      : type === 'success'
      ? 'text-green-600'
      : 'text-blue-600';

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className={`${bgColor} border rounded-lg shadow-lg p-4 flex items-start space-x-3 max-w-md`}
    >
      <div className={iconColor}>
        {type === 'error' || type === 'warning' ? (
          type === 'error' ? (
            <AlertCircle className="h-5 w-5" />
          ) : (
            <AlertTriangle className="h-5 w-5" />
          )
        ) : null}
      </div>
      <div className="flex-1">
        <p className={`text-sm font-medium ${textColor}`}>{message}</p>
      </div>
      <button
        onClick={onClose}
        className={`${iconColor} hover:opacity-70 transition-opacity`}
      >
        <X className="h-4 w-4" />
      </button>
    </motion.div>
  );
}

// Toast container component
export interface ToastContainerProps {
  toasts: ToastProps[];
  onRemoveToast: (id: string) => void;
}

export function ToastContainer({ toasts, onRemoveToast }: ToastContainerProps) {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      <AnimatePresence>
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} onClose={() => onRemoveToast(toast.id)} />
        ))}
      </AnimatePresence>
    </div>
  );
}
